import React from 'react';
import { Globe, Plane, ShieldCheck, CheckCircle2, ArrowRight, Phone, FileText, Calculator, HelpCircle, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead, { ADVISOR_NAME, ADVISOR_PHONE } from '../components/SEOHead';
import { trackWhatsAppClick } from '../services/analytics';

const nriFaqsEn = [
  {
    q: "Can NRIs buy LIC insurance without traveling to India?",
    a: "Yes. Under LIC's official Mail Order Business (MOB) procedure, NRIs can complete proposal forms, submit foreign residence KYC, undergo medicals in their country of residence, and remit premiums via NRE/NRO accounts entirely from abroad."
  },
  {
    q: "Are LIC maturity proceeds taxable in India for NRIs?",
    a: "Under Section 10(10D) of the Indian Income Tax Act 1961, maturity proceeds from eligible traditional life policies are 100% tax-free in India. Under Double Taxation Avoidance Agreements (DTAA) with the US, UK, UAE, etc., repatriation through NRE bank accounts is smooth and legitimate."
  },
  {
    q: "What is the difference between purchasing via NRE vs NRO account?",
    a: "Premiums paid through an NRE (Non-Resident External) account allow 100% tax-free maturity repatriation back to your overseas account in foreign currency (USD, AED, GBP). Premiums paid via NRO account are repatriable up to USD 1 Million per financial year under RBI guidelines."
  },
  {
    q: "Can NRIs purchase health insurance for dependent parents in Hyderabad?",
    a: "Yes. NRIs can easily purchase HDFC ERGO Optima Senior or Optima Secure covering their elderly parents living in Hyderabad or across India, ensuring complete cashless hospitalization without having to fly down for claims."
  }
];

const nriFaqsTe = [
  {
    q: "ఎన్‌ఆర్‌ఐ (NRI)లు భారత్‌కు రాకుండానే ఎల్‌ఐసి పాలసీ తీసుకోవచ్చా?",
    a: "అవును. ఎల్‌ఐసి అధికారిక మెయిల్ ఆర్డర్ బిజినెస్ (MOB) విధానం ద్వారా విదేశాల్లో ఉన్న భారతీయులు తమ స్వదేశం నుండే దరఖాస్తు ఫారాలు, కేవైసీ సమర్పించి, ఎన్‌ఆర్‌ఈ/ఎన్‌ఆర్‌ఓ ఖాతాల ద్వారా ప్రీమియం చెల్లించి పాలసీ పొందవచ్చు."
  },
  {
    q: "ఎన్‌ఆర్‌ఐలకు ఎల్‌ఐసి మెచ్యూరిటీపై భారతదేశంలో పన్ను ఉంటుందా?",
    a: "భారత ఆదాయపు పన్ను చట్టం సెక్షన్ 10(10D) ప్రకారం అర్హత కలిగిన సాంప్రదాయ ఎల్‌ఐసి పాలసీల మెచ్యూరిటీ మొత్తం భారతదేశంలో 100% పన్ను రహితం. ద్వంద్వ పన్నుల నివారణ ఒప్పందం (DTAA) ప్రకారం విదేశాలకు నిధులను సురక్షితంగా బదిలీ చేసుకోవచ్చు."
  },
  {
    q: "ఎన్‌ఆర్‌ఈ (NRE) మరియు ఎన్‌ఆర్‌ఓ (NRO) ఖాతాల మధ్య తేడా ఏమిటి?",
    a: "ఎన్‌ఆర్‌ఈ ఖాతా నుండి ప్రీమియం చెల్లిస్తే మెచ్యూరిటీ మొత్తాన్ని 100% విదేశీ కరెన్సీలో (USD, AED మొదలైనవి) పూర్తి పన్ను రహితంగా విదేశాలకు తీసుకెళ్లవచ్చు. ఎన్‌ఆర్‌ఓ ఖాతా ద్వారా చెల్లిస్తే ఆర్‌బిఐ నిబంధనల ప్రకారం సంవత్సరానికి 1 మిలియన్ డాలర్ల వరకు బదిలీ చేసుకునే వెసులుబాటు ఉంటుంది."
  },
  {
    q: "హైదరాబాద్‌లో ఉన్న తల్లిదండ్రుల కోసం ఎన్‌ఆర్‌ఐలు హెల్త్ ఇన్సూరెన్స్ తీసుకోవచ్చా?",
    a: "తప్పకుండా. విదేశాల్లో ఉంటున్న ఎన్‌ఆర్‌ఐలు హైదరాబాద్ లేదా దేశంలోని ఇతర నగరాల్లో ఉన్న తమ వృద్ధ తల్లిదండ్రుల కోసం హెచ్‌డిఎఫ్‌సి ఎర్గో ఆప్టిమా సీనియర్ లేదా ఆప్టిమా సెక్యూర్ పాలసీని తీసుకోవచ్చు. తద్వారా 14,000+ ఆసుపత్రులలో నగదు రహిత అత్యవసర చికిత్స లభిస్తుంది."
  }
];

const NRIInsurance = () => {
  const { t, i18n } = useTranslation();
  const isTe = i18n.language === 'te';
  const currentFaqs = isTe ? nriFaqsTe : nriFaqsEn;

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': isTe ? 'ఎన్‌ఆర్‌ఐ బీమా సలహాలు & మెయిల్ ఆర్డర్ బిజినెస్ (MOB)' : 'NRI Insurance Advisory & Mail Order Business (MOB)',
    'description': 'Specialized LIC life insurance and health insurance services for Non-Resident Indians (NRIs) in USA, UAE, UK, Singapore, Canada, and Gulf countries.',
    'provider': {
      '@type': 'InsuranceAgency',
      'name': 'VSR Insurance Plus'
    },
    'areaServed': ['USA', 'UAE', 'United Kingdom', 'Singapore', 'Canada', 'Saudi Arabia', 'Qatar', 'Oman', 'Australia']
  };

  return (
    <div className="min-h-screen pt-28 pb-20 bg-slate-50 dark:bg-slate-950">
      <SEOHead
        title={isTe ? "ఎన్‌ఆర్‌ఐల కోసం ఎల్‌ఐసి బీమా | మెయిల్ ఆర్డర్ బిజినెస్ (MOB) | VSR Insurance Plus" : "LIC Life Insurance for NRIs | Mail Order Business (MOB) & FEMA Guide | VSR Insurance Plus"}
        description={isTe ? "భారత్‌కు రాకుండానే ఎల్‌ఐసి పాలసీలు పొందే మెయిల్ ఆర్డర్ బిజినెస్ (MOB) విధానం. ఎన్‌ఆర్‌ఈ/ఎన్‌ఆర్‌ఓ ఖాతాల ద్వారా చెల్లింపులు, ఫెమా నిబంధనలు మరియు పన్ను రహిత బదిలీ. సీనియర్ అడ్వైజర్ వి. ఎస్. రెడ్డి." : "Complete NRI insurance guidance by V. S. Reddy. 100% remote Mail Order Business (MOB) verification, NRE/NRO foreign remittance, FEMA compliance, and DTAA tax-free repatriation."}
        canonicalPath="/nri-insurance"
        schemaJson={pageSchema}
        breadcrumbs={[{ name: isTe ? 'ఎన్‌ఆర్‌ఐ బీమా' : 'NRI Insurance Services', path: '/nri-insurance' }]}
      />

      <div className="container mx-auto px-6 max-w-6xl">
        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 p-2.5 px-4 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            <Globe className="w-4 h-4" /> {isTe ? "గ్లోబల్ ఎన్‌ఆర్‌ఐ సేవలు • USA, UAE, UK, సింగపూర్ & కెనడా" : "Global NRI Advisory • USA, UAE, UK, Singapore & Canada"}
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 dark:text-white mb-6 tracking-tight">
            {isTe ? "విదేశీ భారతీయుల (NRI) కోసం ఎల్‌ఐసి జీవిత బీమా" : "LIC Life Insurance for Non-Resident Indians (NRIs)"}
          </h1>
          <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            {isTe
              ? "భారత్‌కు రావలసిన అవసరం లేకుండా మీ స్వదేశం నుండే కేంద్ర ప్రభుత్వ సార్వభౌమ హామీ గల జీవిత బీమా మరియు గ్యారెంటీడ్ రిటైర్‌మెంట్ పెన్షన్ పొందండి. ఆర్‌బిఐ, ఫెమా మరియు ఐఆర్‌డిఎఐ నిబంధనలకు అనుగుణంగా పూర్తి డిజిటల్ సేవలు."
              : "Secure sovereign-backed life protection and guaranteed retirement income back home in India without traveling. Fully compliant with RBI, FEMA, and IRDAI regulations."}
          </p>
        </div>

        {/* 4 Core Advantages for NRIs */}
        <div className="grid md:grid-cols-4 gap-6 mb-16">
          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <Plane className="w-8 h-8 text-orange-500 mb-3" />
            <h3 className="font-bold text-slate-900 dark:text-white mb-1 text-base">
              {isTe ? "భారత్‌కు రానవసరం లేదు" : "No Travel Required"}
            </h3>
            <p className="text-slate-500 text-xs leading-relaxed">
              {isTe ? "మీరు నివసిస్తున్న దేశం నుండే మెయిల్ ఆర్డర్ బిజినెస్ (MOB) ద్వారా పూర్తి ప్రక్రియను పూర్తి చేయవచ్చు." : "Complete your verification through the Mail Order Business (MOB) route from your home country."}
            </p>
          </div>

          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <ShieldCheck className="w-8 h-8 text-emerald-500 mb-3" />
            <h3 className="font-bold text-slate-900 dark:text-white mb-1 text-base">
              {isTe ? "కేంద్ర ప్రభుత్వ హామీ" : "Sovereign Guarantee"}
            </h3>
            <p className="text-slate-500 text-xs leading-relaxed">
              {isTe ? "ఎల్‌ఐసి చట్టం సెక్షన్ 37 ప్రకారం మీ పెట్టుబడికి మరియు బోనస్‌లకు 100% కేంద్ర ప్రభుత్వ హామీ." : "100% of sum assured and bonuses are legally guaranteed by the Government of India under Sec 37 of the LIC Act."}
            </p>
          </div>

          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <Lock className="w-8 h-8 text-primary mb-3" />
            <h3 className="font-bold text-slate-900 dark:text-white mb-1 text-base">
              {isTe ? "పన్ను రహిత బదిలీ" : "Tax-Free Repatriation"}
            </h3>
            <p className="text-slate-500 text-xs leading-relaxed">
              {isTe ? "ఎన్‌ఆర్‌ఈ ఖాతా ద్వారా చెల్లించిన ప్రీమియంల మెచ్యూరిటీ నిధులను డాలర్లు/దీర్ఘాల్లో విదేశాలకు తీసుకెళ్లవచ్చు." : "Proceeds from NRE accounts are fully repatriable to your overseas bank accounts under RBI & FEMA guidelines."}
            </p>
          </div>

          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <Calculator className="w-8 h-8 text-rose-500 mb-3" />
            <h3 className="font-bold text-slate-900 dark:text-white mb-1 text-base">
              {isTe ? "ఎన్‌ఆర్‌ఐ హెల్త్ కాలిక్యులేటర్" : "NRI Premium Calculator"}
            </h3>
            <p className="text-slate-500 text-xs leading-relaxed">
              {isTe ? "విదేశీ నివాసితులకు కరెన్సీ మార్పిడి మరియు ఖచ్చితమైన ప్రీమియం లెక్కలు." : "Model precise premium requirements in INR or foreign currencies (USD, AED, GBP)."}
            </p>
          </div>
        </div>

        {/* Step-by-Step Mail Order Business (MOB) Process */}
        <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 md:p-12 border border-slate-200 dark:border-slate-800 shadow-sm mb-16">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold text-primary uppercase tracking-widest block mb-2">
              {isTe ? "అధికారిక ప్రక్రియ" : "Official Procedure"}
            </span>
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 dark:text-white mb-3">
              {isTe ? "ఎల్‌ఐసి మెయిల్ ఆర్డర్ బిజినెస్ (MOB) 4 దశలు" : "How LIC Mail Order Business (MOB) Works"}
            </h2>
            <p className="text-slate-500 text-sm">
              {isTe ? "విదేశాల్లో ఉంటూనే అత్యంత సురక్షితంగా పాలసీ పొందే సరళమైన పద్ధతి." : "A secure, streamlined workflow coordinated directly by Senior Advisor V. S. Reddy."}
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="p-6 bg-slate-50 dark:bg-slate-800/60 rounded-2xl relative">
              <span className="text-4xl font-black text-slate-200 dark:text-slate-700 block mb-2">01</span>
              <h3 className="font-bold text-slate-900 dark:text-white text-base mb-2">
                {isTe ? "సంప్రదింపు & విశ్లేషణ" : "Policy Selection"}
              </h3>
              <p className="text-slate-500 text-xs leading-relaxed">
                {isTe ? "వీడియో లేదా వాట్సాప్ కాల్ ద్వారా మీ ఆర్థిక లక్ష్యాలకు తగిన ఎల్‌ఐసి పథకాన్ని ఎంచుకోవడం." : "Consult over video/WhatsApp to select the ideal endowment, term, or retirement plan."}
              </p>
            </div>

            <div className="p-6 bg-slate-50 dark:bg-slate-800/60 rounded-2xl relative">
              <span className="text-4xl font-black text-slate-200 dark:text-slate-700 block mb-2">02</span>
              <h3 className="font-bold text-slate-900 dark:text-white text-base mb-2">
                {isTe ? "కేవైసీ & ధృవీకరణ" : "KYC Attestation"}
              </h3>
              <p className="text-slate-500 text-xs leading-relaxed">
                {isTe ? "పాస్‌పోర్ట్, వీసా, మరియు విదేశీ ఉద్యోగ పత్రాల ధృవీకరణ సమర్పణ." : "Submit verified copies of passport, valid resident visa, and proof of overseas employment."}
              </p>
            </div>

            <div className="p-6 bg-slate-50 dark:bg-slate-800/60 rounded-2xl relative">
              <span className="text-4xl font-black text-slate-200 dark:text-slate-700 block mb-2">03</span>
              <h3 className="font-bold text-slate-900 dark:text-white text-base mb-2">
                {isTe ? "విదేశీ మెడికల్స్ (అవసరమైతే)" : "Overseas Medicals"}
              </h3>
              <p className="text-slate-500 text-xs leading-relaxed">
                {isTe ? "మీ స్థానిక దేశంలోనే ఎల్‌ఐసి ఆమోదిత క్లినిక్‌లలో సాధారణ పరీక్షలు పూర్తి చేయడం." : "Undergo standard health tests at recognized local diagnostic clinics in your city of residence."}
              </p>
            </div>

            <div className="p-6 bg-slate-50 dark:bg-slate-800/60 rounded-2xl relative">
              <span className="text-4xl font-black text-slate-200 dark:text-slate-700 block mb-2">04</span>
              <h3 className="font-bold text-slate-900 dark:text-white text-base mb-2">
                {isTe ? "ఆన్‌లైన్ చెల్లింపు & బాండ్" : "Remittance & Issuance"}
              </h3>
              <p className="text-slate-500 text-xs leading-relaxed">
                {isTe ? "ఎన్‌ఆర్‌ఈ/ఎన్‌ఆర్‌ఓ ద్వారా ఆన్‌లైన్ పేమెంట్; అధికారిక పాలసీ బాండ్ మీ ఈమెయిల్ మరియు చిరునామాకు జారీ." : "Transfer premium directly via NRE/NRO; original policy bond dispatched electronically and by courier."}
              </p>
            </div>
          </div>
        </div>

        {/* NRI FAQs */}
        <div className="max-w-3xl mx-auto mb-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 dark:text-white mb-2 flex items-center justify-center gap-2">
              <HelpCircle className="w-6 h-6 text-primary" /> {isTe ? "ఎన్‌ఆర్‌ఐలకు తరచుగా వచ్చే సందేహాలు (FAQ)" : "NRI Insurance FAQs"}
            </h2>
            <p className="text-slate-500 text-sm">
              {isTe ? "సీనియర్ అడ్వైజర్ వి. ఎస్. రెడ్డి గారి అధికారిక వివరణలు." : "Clear answers regarding FEMA, tax implications, and claim processing."}
            </p>
          </div>

          <div className="space-y-4">
            {currentFaqs.map((faq, i) => (
              <div key={i} className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
                <h3 className="font-bold text-slate-900 dark:text-white text-base mb-2">{faq.q}</h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Outro NRI Consultation Callout */}
        <div className="bg-gradient-to-r from-orange-600 to-amber-600 rounded-3xl p-8 md:p-12 text-white shadow-xl flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="max-w-xl">
            <span className="text-xs font-bold text-orange-200 uppercase tracking-widest block mb-2">
              {isTe ? "ప్రత్యేక సలహాదారు" : "Dedicated NRI Advisory"}
            </span>
            <h2 className="text-2xl md:text-3xl font-extrabold mb-3">
              {isTe ? "విదేశాల్లో ఉన్నారా? నేడే మీ ప్రణాళికను సిద్ధం చేసుకోండి" : "Living Abroad? Plan Your Sovereign Indian Portfolio"}
            </h2>
            <p className="text-orange-100 text-sm leading-relaxed mb-4">
              {isTe
                ? "అమెరికా, గల్ఫ్, యూరోప్ మరియు ఆస్ట్రేలియాలోని ప్రవాస భారతీయులకు 25 సంవత్సరాలుగా విశ్వసనీయ ఎల్‌ఐసి సేవలు అందిస్తున్న సీనియర్ అడ్వైజర్ వి. ఎస్. రెడ్డి గారితో నేరుగా మాట్లాడండి."
                : "Connect directly with V. S. Reddy to evaluate LIC Jeevan Utsav (10% lifetime pension) or secure health coverage for your dependent parents back in Hyderabad."}
            </p>
            <div className="flex items-center gap-3 text-xs text-orange-200">
              <CheckCircle2 className="w-4 h-4 text-white" />
              <span>{isTe ? "100% ఉచిత ఎన్‌ఆర్‌ఐ సలహా" : "100% Free Consultation — Zero Brokerage Fees"}</span>
            </div>
          </div>

          <div className="flex flex-col gap-3 w-full md:w-auto">
            <a
              href={`https://wa.me/919849664800?text=${encodeURIComponent(isTe ? 'నమస్కారం వి. ఎస్. రెడ్డి గారు, నేను విదేశీ ఎన్‌ఆర్‌ఐని. ఎల్‌ఐసి మెయిల్ ఆర్డర్ పాలసీల గురించి సంప్రదిస్తున్నాను.' : 'Hello V. S. Reddy, I am an NRI living abroad and would like to consult on LIC policies via Mail Order Business.')}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick('nri_hub_whatsapp')}
              className="px-8 py-3.5 bg-white text-orange-700 hover:bg-orange-50 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg transition-transform hover:-translate-y-0.5"
            >
              <Phone className="w-4 h-4 text-emerald-600" /> {isTe ? "వాట్సాప్‌లో సంప్రదించండి" : "WhatsApp Advisor Direct"}
            </a>
            <Link
              to="/nri-calculator"
              className="px-8 py-3.5 bg-black/20 hover:bg-black/30 text-white rounded-xl font-medium text-center border border-white/20 transition-colors text-sm"
            >
              {isTe ? "ఎన్‌ఆర్‌ఐ కాలిక్యులేటర్ తెరవండి" : "Open NRI Health Calculator"}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NRIInsurance;
